import React, { useState, useEffect } from 'react';
import { useMemoryGame } from './MemoryGameContext';
import { GRID_SIZE } from './types';

export const MemoryHUD: React.FC = () => {
  const { state } = useMemoryGame();
  const { phase, objects, playerPlacements, timeLeft, difficulty, levelStartTime } = state;
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time every second for live timer
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Calculate current stats
  const totalObjects = objects.length;
  const placedObjects = playerPlacements.length;
  const progress = `${placedObjects} / ${totalObjects}`;
  
  // Calculate level time
  const levelTime = levelStartTime ? Math.floor((currentTime - levelStartTime) / 1000) : 0;
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Calculate accuracy during recall phase
  const currentAccuracy = placedObjects > 0 ? 
    ((playerPlacements.filter(placement => {
      const originalObject = objects.find(obj => obj.type === placement.type);
      return originalObject && 
             placement.position.x === originalObject.position.x && 
             placement.position.y === originalObject.position.y;
    }).length / placedObjects) * 100).toFixed(1) : 0;

  const getPhaseDisplay = () => {
    switch (phase) {
      case 'memorization':
        return `Memorize Objects (${timeLeft}s left)`;
      case 'recall':
        return `Place Object ${placedObjects + 1}`;
      case 'result':
        return 'Level Complete!';
      default:
        return 'Memory Palace';
    }
  };

  const getObjectTypeToPlace = () => {
    if (phase === 'recall' && placedObjects < totalObjects) {
      return objects[placedObjects].type;
    }
    return '';
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-6">
      {/* Header with current objective */}
      <div className="text-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">
          {getPhaseDisplay()}
        </h2>
        {phase === 'recall' && placedObjects < totalObjects && (
          <div className="text-2xl font-bold text-blue-600">
            Next Object: {getObjectTypeToPlace()}
          </div>
        )}
      </div>

      {/* Current Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-4">
        <div className="text-center">
          <div className="text-sm text-gray-600">Phase Time:</div>
          <div className="text-xl font-bold text-blue-600">
            {phase === 'memorization' ? `${timeLeft}s` : 
             phase === 'recall' ? 'Placing...' : 'Complete'}
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-sm text-gray-600">Level Time:</div>
          <div className="text-xl font-bold text-orange-600">
            {formatTime(levelTime)}
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-sm text-gray-600">Objects:</div>
          <div className="text-xl font-bold text-purple-600">
            {totalObjects}
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-sm text-gray-600">
            {phase === 'recall' ? 'Accuracy:' : 'Progress:'}
          </div>
          <div className="text-xl font-bold text-green-600">
            {phase === 'recall' && placedObjects > 0 ? `${currentAccuracy}%` : progress}
          </div>
        </div>
        
        <div className="text-center">
          <div className="text-sm text-gray-600">Placed:</div>
          <div className="text-xl font-bold text-orange-600">
            {placedObjects} / {totalObjects}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-600 mb-1">
          <span>Progress</span>
          <span>{Math.round((placedObjects / totalObjects) * 100)}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(placedObjects / totalObjects) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Difficulty Settings */}
      <div className="border-t pt-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 text-center">
          Difficulty Level {difficulty}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <div className="text-gray-600">Grid Size:</div>
            <div className="font-semibold">{GRID_SIZE}×{GRID_SIZE}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-600">Objects:</div>
            <div className="font-semibold">{totalObjects}</div>
          </div>
          <div className="text-center">
            <div className="text-gray-600">Memorization:</div>
            <div className="font-semibold">20s</div>
          </div>
        </div>
      </div>
    </div>
  );
};